// src/api/lenderResponseImport.ts
//
// Typed client for the lender-response import admin endpoints.
// Reuses the shared axios instance from ``api/claims.ts`` so auth headers,
// base URL, and 401-handling come for free.

import { client } from './claims';

const BASE = '/ui/admin/lender-responses';

// --- Types -------------------------------------------------------------------

export interface VocabularyField {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'money' | 'yn';
  required: boolean;
}

export type Mapping = Record<string, string | null>;

export interface DuplicateRunInfo {
  runId: string;
  startedAt?: string;
  startedBy?: string;
  batchLabel?: string;
  status?: string;
  counts?: Record<string, number>;
}

export interface PreviewCounts {
  totalRows: number;
  blankReferenceRows: number;
  uniqueClaims: number;
  matchedLive: number;
  matchedCancelled: number;
  unmatched: number;
  outcomeClashes: number;
  claimsReplacingExistingSource: number;
  byOutcome: Record<string, number>;
  agreementNumberPromotions: number;
}

export interface PreviewResult {
  // Sheet selection: a multi-sheet workbook with no choice yet returns only
  // needsSheetSelection + sheets — the user must pick a tab.
  needsSheetSelection?: boolean;
  sheets?: string[];
  sheetUsed?: string;
  // Password-protected workbook: the user must supply the password and
  // preview again. passwordError is set when the one given was wrong.
  needsPassword?: boolean;
  passwordError?: string;

  headerErrors?: string[];
  fileHeaders?: string[];
  vocabulary?: VocabularyField[];
  suggestedMapping?: Mapping;
  effectiveMapping?: Mapping;
  mappingOk?: boolean;
  mappingErrors?: string[];
  effectiveSource?: string;
  fileSha256?: string;
  previewToken?: string;
  duplicateOfRun?: DuplicateRunInfo;

  counts?: PreviewCounts;
  problems?: {
    unmatched: string[];
    outcomeClashes: Array<{ claimId: string; existing: string; incoming: string }>;
    cancelledClaims: string[];
  };
  sampleChanges?: Array<{
    claimId: string;
    outcome: string;
    responses: number;
    lenderAgreementNumber: string;
    claimWasCancelled: boolean;
  }>;
}

export interface ImportRun {
  id: string;
  fileName?: string;
  sheet?: string;
  batchLabel?: string;
  lender?: string;
  receivedDate?: string;
  startedBy?: string;
  status: string;
  totals?: { claims: number; rows: number; blankReferenceRows: number };
  counts?: Record<string, number>;
  processed?: number;
  percent?: number;
  stale?: boolean;
  startedAt?: string;
  finishedAt?: string | null;
}

export interface ImportMetadata {
  batchLabel: string;
  receivedDate: string;
  lender: string;
}

// --- Calls -------------------------------------------------------------------

function formFor(
  file: File,
  meta: ImportMetadata,
  extra: Record<string, string | undefined> = {},
): FormData {
  const form = new FormData();
  form.append('file', file);
  form.append('batchLabel', meta.batchLabel);
  form.append('receivedDate', meta.receivedDate);
  form.append('lender', meta.lender);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined) form.append(k, v);
  });
  return form;
}

// The shared client pins Content-Type: application/json at instance level,
// which would strip the multipart boundary off a FormData post and Flask
// would see no file. Overriding per-request makes axios emit a proper
// multipart/form-data header with the boundary.
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export async function previewImport(
  file: File,
  meta: ImportMetadata,
  opts: {
    sheet?: string;
    mapping?: Mapping;
    ignoredHeaders?: string[];
    password?: string;
  } = {},
): Promise<PreviewResult> {
  const { data } = await client.post<PreviewResult>(
    `${BASE}/preview`,
    formFor(file, meta, {
      password: opts.password || undefined,
      sheet: opts.sheet,
      mapping: opts.mapping ? JSON.stringify(opts.mapping) : undefined,
      ignoredHeaders: opts.ignoredHeaders
        ? JSON.stringify(opts.ignoredHeaders)
        : undefined,
    }),
    MULTIPART,
  );
  return data;
}

export async function commitImport(
  file: File,
  meta: ImportMetadata,
  opts: {
    sheet?: string;
    mapping: Mapping;
    ignoredHeaders?: string[];
    expectedSha256: string;
    expectedPreviewToken: string;
    allowDuplicateFile?: boolean;
    overrideOutcomeClashes?: boolean;
    password?: string;
  },
): Promise<{ runId: string }> {
  const { data } = await client.post<{ runId: string }>(
    `${BASE}/commit`,
    formFor(file, meta, {
      confirm: 'true',
      password: opts.password || undefined,
      sheet: opts.sheet,
      mapping: JSON.stringify(opts.mapping),
      ignoredHeaders: opts.ignoredHeaders
        ? JSON.stringify(opts.ignoredHeaders)
        : undefined,
      expectedSha256: opts.expectedSha256,
      expectedPreviewToken: opts.expectedPreviewToken,
      allowDuplicateFile: opts.allowDuplicateFile ? 'true' : undefined,
      overrideOutcomeClashes: opts.overrideOutcomeClashes ? 'true' : undefined,
    }),
    MULTIPART,
  );
  return data;
}

export async function listRuns(limit = 25): Promise<ImportRun[]> {
  const { data } = await client.get<{ runs: ImportRun[] }>(
    `${BASE}/runs`, { params: { limit } });
  return data.runs;
}

export async function getRun(runId: string): Promise<ImportRun> {
  const { data } = await client.get<ImportRun>(`${BASE}/runs/${runId}`);
  return data;
}

export async function cancelRun(runId: string): Promise<void> {
  await client.post(`${BASE}/runs/${runId}/cancel`);
}

export async function resumeRun(runId: string): Promise<void> {
  await client.post(`${BASE}/runs/${runId}/resume`);
}

/** Authed blob download — a plain <a href> would not carry the bearer token. */
async function downloadAuthed(path: string, fallbackName: string): Promise<void> {
  const resp = await client.get(path, { responseType: 'blob' });
  const disposition = String(resp.headers['content-disposition'] || '');
  const match = disposition.match(/filename="([^"]+)"/);
  const name = match ? match[1] : fallbackName;
  const url = window.URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function downloadRunFile(runId: string): Promise<void> {
  return downloadAuthed(`${BASE}/runs/${runId}/file`, `${runId}.xlsx`);
}

export function downloadRunReport(runId: string): Promise<void> {
  return downloadAuthed(`${BASE}/runs/${runId}/report`, `${runId}-results.csv`);
}
